import { Injectable } from '@angular/core';
import { ApiRouteSchedulingService } from '../../../../api/elite/routes/scheduling.service';
import { forkJoin, Observable } from 'rxjs';
import { User } from '../../../../api/elite/types/users';
import { map } from 'rxjs/operators';
import { FilterSubWorkOrder, FilterWorkOrder } from '../../models/item';
import { FilterDepartmentService } from '@dep/filters/services';
import { FilterSchedulerMachine } from '@dep/filters/models';
import { ApiRouteUserService } from '../../../../api/elite/routes/users.service';
import { ApiRouteDailyPlanningService } from '../../../../api/elite/routes/daily-planning.service';
import { DailyPlanningApiRoutesDto } from '../../../../api/elite/dto/daily-planning.dto';

@Injectable({
  providedIn: 'root',
})
export class FilterSchedulerApiService {
  constructor(
    private readonly schedulerApiRoute: ApiRouteSchedulingService,
    private readonly departmentService: FilterDepartmentService,
    private readonly userApiRoute: ApiRouteUserService,
    private readonly dailyPlanningApiService: ApiRouteDailyPlanningService
  ) {}

  machineData(stage: string): Observable<FilterSchedulerMachine[]> {
    return stage === 'Filter Packing'
      ? this.machineBySubWorkOrderData(stage)
      : this.machineByWorkOrderData(stage);
  }
  machineByWorkOrderData(
    stageName: string
  ): Observable<FilterSchedulerMachine[]> {
    return forkJoin([
      this.schedulerApiRoute.getMachineDataByWorkOrder({
        department: this.departmentService.NAME,
        stageName,
      }),
      this.schedulerApiRoute.getMachineWorkOrdersData({
        department: this.departmentService.NAME,
        stageName,
      }),
    ]).pipe(
      map(([machines, machineWorkOrders]) => {
        return machines.map((ma) => {
          ma.machinesWorkOrders = ma.machinesWorkOrders
            .map((machineWorkOrder) => {
              const item = machineWorkOrders.find(
                (wo) => wo.id === machineWorkOrder.id
              );
              if (!item) {
                return null;
              }
              Object.assign(machineWorkOrder, item);
              return machineWorkOrder;
            })
            .filter((el) => !!el);
          return FilterSchedulerMachine.fromObject(ma as any);
        });
      })
    );
  }

  machineBySubWorkOrderData(
    stageName: string
  ): Observable<FilterSchedulerMachine[]> {
    return this.schedulerApiRoute
      .getMachineDataBySubWorkOrder({
        department: this.departmentService.NAME,
        stageName,
      })
      .pipe(
        map((machines) => {
          return machines.map((ma) => {
            return FilterSchedulerMachine.fromObject(ma as any);
          });
        })
      );
  }

  workOrderData(): Observable<FilterWorkOrder[]> {
    return this.schedulerApiRoute
      .getWorkOrderData({
        department: this.departmentService.NAME,
      })
      .pipe(
        map((workOrders) =>
          workOrders.map((wo) => FilterWorkOrder.fromObject(wo as any))
        )
      );
  }

  subWorkOrderData(stage: string): Observable<FilterSubWorkOrder[]> {
    return this.schedulerApiRoute
      .getSubWorkOrderData({
        department: this.departmentService.NAME,
        stage: [stage],
      })
      .pipe(
        map((sub) => sub.map((s) => FilterSubWorkOrder.fromObject(s as any)))
      );
  }

  operators(): Observable<User[]> {
    return this.userApiRoute.allUsersV2(
      this.departmentService.NAME,
      'Operador'
    );
  }

  currentDayPlanning(): Observable<
    DailyPlanningApiRoutesDto['getAllPlanningQuantity']['return']
  > {
    const date = new Date();

    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);

    return this.dailyPlanningApiService.getAllRecords({
      departmentName: this.departmentService.NAME,
      startDate,
      endDate,
      isFinished: false,
    });
  }
}
